import * as React from 'react';
import {
  ChevronDown,
  ChevronRight,
  Database,
  Table2,
  Search,
  Plug,
  Circle,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface EntityNode {
  id: string;
  name: string;
  recordCount?: number;
  icon?: React.ReactNode;
}

export interface ConnectorNode {
  id: string;
  name: string;
  type: string; // e.g., 'directus', 'mssql', 'mysql'
  status: 'connected' | 'disconnected' | 'error';
  icon?: React.ReactNode;
  entities: EntityNode[];
}

export interface ConnectorSidebarProps {
  connectors: ConnectorNode[];
  selectedEntityId?: string;
  onEntitySelect: (connectorId: string, entityId: string) => void;
  isLoading?: boolean;
  className?: string;
}

const statusColorMap: Record<
  'connected' | 'disconnected' | 'error',
  { dot: string; badge: 'success' | 'secondary' | 'destructive' }
> = {
  connected: {
    dot: 'bg-[var(--color-success)]',
    badge: 'success',
  },
  disconnected: {
    dot: 'bg-[var(--color-secondary)]',
    badge: 'secondary',
  },
  error: {
    dot: 'bg-[var(--color-destructive)]',
    badge: 'destructive',
  },
};

const getConnectorIcon = (type: string): React.ReactNode => {
  const iconProps = {
    className: 'h-4 w-4',
  };

  switch (type.toLowerCase()) {
    case 'directus':
    case 'hros':
      return <Database {...iconProps} />;
    case 'mssql':
    case 'mysql':
    case 'postgres':
    case 'sql':
      return <Database {...iconProps} />;
    default:
      return <Plug {...iconProps} />;
  }
};

const ConnectorSkeleton: React.FC = () => (
  <div className="space-y-3 px-3 py-2">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <div className="space-y-2 pl-4">
          {Array.from({ length: 2 }).map((_, j) => (
            <Skeleton key={j} className="h-8 w-[90%]" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ConnectorItem: React.FC<{
  connector: ConnectorNode;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedEntityId?: string;
  onEntitySelect: (connectorId: string, entityId: string) => void;
}> = ({
  connector,
  isExpanded,
  onToggleExpand,
  selectedEntityId,
  onEntitySelect,
}) => {
  const statusConfig = statusColorMap[connector.status];

  return (
    <div className="space-y-1">
      {/* Connector header */}
      <button
        onClick={onToggleExpand}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
          'transition-colors hover:bg-[var(--color-accent)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
        )}
      >
        {/* Expand/collapse chevron */}
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>

        {/* Connector icon */}
        <div className="flex-shrink-0 text-[var(--color-muted-foreground)]">
          {connector.icon || getConnectorIcon(connector.type)}
        </div>

        {/* Connector name */}
        <span className="flex-1 truncate text-left text-[var(--color-foreground)]">
          {connector.name}
        </span>

        {/* Status indicator - dot + badge */}
        <div className="flex-shrink-0 flex items-center gap-1">
          <div
            className={cn('h-2 w-2 rounded-full', statusConfig.dot)}
            title={`Status: ${connector.status}`}
          />
        </div>
      </button>

      {/* Entities list - expanded */}
      {isExpanded && (
        <div className="space-y-1 pl-4">
          {connector.entities.length === 0 ? (
            <div className="rounded-md px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
              No entities available
            </div>
          ) : (
            connector.entities.map((entity) => (
              <button
                key={entity.id}
                onClick={() => onEntitySelect(connector.id, entity.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                  'transition-colors hover:bg-[var(--color-accent)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
                  selectedEntityId === entity.id
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                    : 'text-[var(--color-foreground)]',
                )}
              >
                {/* Entity icon */}
                <div className="flex-shrink-0 text-[var(--color-muted-foreground)]">
                  {entity.icon || <Table2 className="h-4 w-4" />}
                </div>

                {/* Entity name */}
                <span className="flex-1 truncate text-left">
                  {entity.name}
                </span>

                {/* Record count badge */}
                {entity.recordCount !== undefined && (
                  <Badge
                    variant="outline"
                    className="flex-shrink-0 text-xs bg-[var(--color-muted)]/50"
                  >
                    {entity.recordCount}
                  </Badge>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const ConnectorSidebar = React.forwardRef<
  HTMLDivElement,
  ConnectorSidebarProps
>(
  (
    {
      connectors,
      selectedEntityId,
      onEntitySelect,
      isLoading = false,
      className,
    },
    ref,
  ) => {
    const [expandedConnectors, setExpandedConnectors] = React.useState<
      Set<string>
    >(new Set(connectors.map((c) => c.id)));
    const [searchTerm, setSearchTerm] = React.useState('');

    const toggleConnectorExpand = (connectorId: string) => {
      setExpandedConnectors((prev) => {
        const next = new Set(prev);
        if (next.has(connectorId)) {
          next.delete(connectorId);
        } else {
          next.add(connectorId);
        }
        return next;
      });
    };

    // Filter connectors and entities based on search term
    const filteredConnectors = React.useMemo(() => {
      if (!searchTerm.trim()) {
        return connectors;
      }

      const lowerSearch = searchTerm.toLowerCase();

      return connectors
        .map((connector) => ({
          ...connector,
          entities: connector.entities.filter((entity) =>
            entity.name.toLowerCase().includes(lowerSearch),
          ),
        }))
        .filter(
          (connector) =>
            connector.name.toLowerCase().includes(lowerSearch) ||
            connector.entities.length > 0,
        );
    }, [connectors, searchTerm]);

    return (
      <div
        ref={ref}
        className={cn(
          'flex h-full flex-col overflow-hidden bg-[var(--color-background)] text-[var(--color-foreground)]',
          className,
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-[var(--color-border)] px-3 py-3">
          <h2 className="mb-3 text-sm font-semibold text-[var(--color-foreground)]">
            Connectors
          </h2>

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
            <Input
              placeholder="Search entities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'pl-8 h-9',
                'bg-[var(--color-accent)] border-[var(--color-border)]',
                'placeholder:text-[var(--color-muted-foreground)]',
                'focus-visible:ring-[var(--color-primary)]',
              )}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <ConnectorSkeleton />
          ) : filteredConnectors.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Database className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)] opacity-50 mb-2" />
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {searchTerm
                    ? 'No entities found'
                    : 'No connectors available'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2 px-3 py-2">
              {filteredConnectors.map((connector) => (
                <ConnectorItem
                  key={connector.id}
                  connector={connector}
                  isExpanded={expandedConnectors.has(connector.id)}
                  onToggleExpand={() => toggleConnectorExpand(connector.id)}
                  selectedEntityId={selectedEntityId}
                  onEntitySelect={onEntitySelect}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - optional status info */}
        {!isLoading && filteredConnectors.length > 0 && (
          <div className="flex-shrink-0 border-t border-[var(--color-border)] px-3 py-2">
            <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
              <div className="flex items-center gap-1">
                <Circle className="h-2 w-2 fill-[var(--color-success)]" />
                <span>Connected</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Circle className="h-2 w-2 fill-[var(--color-secondary)]" />
                <span>Disconnected</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

ConnectorSidebar.displayName = 'ConnectorSidebar';
